import React, { useEffect, useState, useSyncExternalStore } from 'react';
import { WidgetView } from '../widget/views';
import { isPhone, type SiteChatController } from './controller';

/** The live widget on the visitor's page. */
export function SiteChatApp({ controller, win }: { controller: SiteChatController; win: Window }) {
    const model = useSyncExternalStore(controller.subscribe, controller.getModel);
    const [phone, setPhone] = useState(() => isPhone(win));
    const [viewport, setViewport] = useState<PhoneViewport | null>(null);

    useEffect(() => {
        const query = win.matchMedia?.('(max-width: 759px)');
        if (!query)
            return;
        const update = () => setPhone(query.matches);
        query.addEventListener?.('change', update);
        return () => query.removeEventListener?.('change', update);
    }, [win]);

    // On a phone the open panel follows the visual viewport, so the keyboard shrinks the panel instead of
    // covering the composer. Its top follows too: iOS pans the visual viewport down to the focused field, and a
    // panel pinned to the top of the layout viewport slid up under the address bar with its header.
    useEffect(() => {
        const visual = win.visualViewport;
        if (!phone || !model.open || !visual) {
            setViewport(null);
            return;
        }
        const update = () => setViewport(previous => {
            const next = { top: Math.max(0, Math.round(visual.offsetTop)), height: Math.round(visual.height) };
            return previous?.top === next.top && previous.height === next.height ? previous : next;
        });
        update();
        visual.addEventListener('resize', update);
        visual.addEventListener('scroll', update);
        return () => {
            visual.removeEventListener('resize', update);
            visual.removeEventListener('scroll', update);
        };
    }, [win, phone, model.open]);

    // The panel covers the whole phone screen: the page under it must not scroll along with the thread (or be
    // scrolled by the browser to the focused field). Restored exactly as the page had it.
    useEffect(() => {
        if (!phone || !model.open)
            return;
        const root = win.document.documentElement;
        const body = win.document.body;
        const before = { root: root.style.overflow, body: body?.style.overflow ?? '' };
        root.style.overflow = 'hidden';
        if (body)
            body.style.overflow = 'hidden';
        return () => {
            root.style.overflow = before.root;
            if (body)
                body.style.overflow = before.body;
        };
    }, [win, phone, model.open]);

    if (!controller.visible)
        return null;

    return <WidgetView model={model} frame="page" phone={phone} viewport={viewport} />;
}

/** The visible part of the page on a phone (above the keyboard), in the layout viewport's pixels. */
export type PhoneViewport = { top: number; height: number };
